import { useEffect, useMemo, useRef, useState } from 'react';
import type { TaskEntry } from '~/lib/workflows/useWorkflows';
import { TaskPanel } from './TaskPanel';

type TasksListProps = {
  tasks: TaskEntry[];
  className?: string;
};

export const TasksList = ({ tasks, className = '' }: TasksListProps) => {
  const [newTaskIds, setNewTaskIds] = useState<Set<string>>(new Set());
  const prevTaskIdsRef = useRef<Set<string>>(new Set());

  const sortedTasks = useMemo(() => {
    return [...tasks].sort(
      (a, b) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    );
  }, [tasks]);

  useEffect(() => {
    const currentTaskIds = new Set(
      sortedTasks.map((task) => `${task.workflowId}-${task.taskName}`),
    );

    const newTasks = new Set<string>();

    currentTaskIds.forEach((taskId) => {
      if (!prevTaskIdsRef.current.has(taskId)) {
        newTasks.add(taskId);
      }
    });

    if (newTasks.size > 0) {
      setNewTaskIds(newTasks);

      const timer = setTimeout(() => {
        setNewTaskIds(new Set());
      }, 60);

      prevTaskIdsRef.current = currentTaskIds;
      return () => clearTimeout(timer);
    }

    prevTaskIdsRef.current = currentTaskIds;
  }, [sortedTasks]);

  return (
    <div className={`flex flex-column gap-3 ${className}`}>
      {sortedTasks.map((taskEntry) => {
        const taskId = `${taskEntry.workflowId}-${taskEntry.taskName}`;
        const isNew = newTaskIds.has(taskId);

        return (
          <TaskPanel
            key={taskId}
            taskName={taskEntry.taskName}
            taskState={taskEntry.taskState}
            startedAt={taskEntry.startedAt}
            isNew={isNew}
          />
        );
      })}
    </div>
  );
};
